import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type ReactNode,
} from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { MAX_UPLOAD_BYTES, type ISODate } from '@app/shared'

import {
  isDemoMode,
  useCapturedLectures,
  useClock,
  useCourses,
  useUploadLecture,
  useUploadQueue,
  type CapturedLecture,
  type RegisteredUpload,
} from '../lib/data'
import { clockLength } from '../lib/time'
import type { QueueEntry } from '../lib/upload-queue'
import { DataModeBadge } from '../components/HeaderBand'
import { RecordingRow } from '../components/recordings/RecordingRow'

// ============================================================================
// UPLOAD A LECTURE — the screen that ends the Quick-Share detour.
//
// THE PROBLEM IT SOLVES. The owner records every lecture on his S24 with Samsung Voice Recorder.
// Until now the file reached LifeOS by being Quick-Shared to a folder on the desktop PC, which
// means the pipeline only ran when he was home, at the machine, and remembered. This screen (and
// the share target in the manifest, which lands here with the file already picked) makes the
// phone the whole path: pick, choose the course, send.
//
// NO MOCKUP SOURCE, AND IT IS THE FIRST MOBILE-FIRST SCREEN. The handoff is ten desktop screens;
// none of them is this one. So it follows the token and type system exactly and departs from the
// handoff in the one way it has to: a single column, 44px-plus touch targets, and controls the
// operating system already knows how to render on a phone (`<input type="file">` opens the
// Android picker, `<input type="date">` opens the Android date wheel). Nothing here reimplements
// a native control on a screen that will mostly be used one-handed on a bus.
//
// THE THREE THINGS IT REFUSES TO DO
//
// 1. IT DOES NOT SHOW FAKE PROGRESS. The bar is XMLHttpRequest's own upload events (see
//    lib/data/remote.ts): it stops when the bytes stop. A 100 MB lecture over phone data is
//    minutes long, and a bar animated on a timer would keep filling after the connection died —
//    which is exactly how a lecture gets lost while the screen says everything is fine.
//
// 2. IT DOES NOT DROP A RECORDING QUIETLY. A failed upload keeps the file picked and offers one
//    retry, and the retry reuses the SAME job — that is what the two-call intake contract is for
//    (apps/worker/src/routes/audio.ts). Cancelling is not a failure and is not dressed as one.
//
// 3. IT DOES NOT GUESS THE COURSE. "No course — assign later" is a first-class choice, because a
//    lecture filed under the wrong course poisons that course's study material and a wrong answer
//    with a citation is worse than no answer (apps/worker/src/routes/lectures.ts). Anything
//    unfiled is listed below and is one tap from being filed.
// ============================================================================

/** '' is the select's "no course" value — a real choice, not an unset field. */
const NO_COURSE = ''

/**
 * Containers a lecture actually arrives in when the operating system gives us no MIME type.
 *
 * `<input accept="audio/*">` filters the picker for us, but a DRAG carries whatever the file
 * manager chose to say — and Windows Explorer routinely hands over `.m4a` (the Samsung Voice
 * Recorder format, the one that matters most here) with an empty `type`, while some managers
 * label an audio-only MP4 as `video/mp4`. Refusing a real recording is worse than accepting an
 * odd file: the Worker derives the stored extension from the filename when the type is unhelpful
 * (apps/worker/src/routes/audio.ts → `extensionFor`), so a wrong guess here costs nothing.
 */
const AUDIO_EXTENSIONS = [
  '.m4a', '.mp3', '.wav', '.aac', '.ogg', '.oga', '.opus', '.flac',
  '.amr', '.3gp', '.3gpp', '.mp4', '.mkv', '.webm', '.wma', '.aif', '.aiff', '.caf',
]

function looksLikeAudio(file: File): boolean {
  if (file.type.startsWith('audio/')) return true
  if (file.type !== '' && !file.type.startsWith('video/')) return false
  const name = file.name.toLowerCase()
  return AUDIO_EXTENSIONS.some((extension) => name.endsWith(extension))
}

/** A drag carrying actual files, rather than selected text or a link. */
function carriesFiles(transfer: DataTransfer | null): boolean {
  return transfer ? Array.from(transfer.types).includes('Files') : false
}

export function RecordLecture() {
  const [params, setParams] = useSearchParams()
  const { data: clock } = useClock()
  const { data: courses, error: coursesError } = useCourses()
  const { data: recent } = useCapturedLectures(8)
  const upload = useUploadLecture()
  // The durable queue. It owns everything that arrived through the Android share sheet; the
  // picker below still does its own watched, one-attempt upload, because a person standing in
  // front of a progress bar wants the bar, not a row that will get to it.
  const queue = useUploadQueue(clock?.date)

  const [file, setFile] = useState<File | null>(null)
  const [durationSec, setDurationSec] = useState<number | null>(null)
  const [courseCode, setCourseCode] = useState<string>(NO_COURSE)
  const [date, setDate] = useState<string>('')
  const [sent, setSent] = useState(0)
  const [problem, setProblem] = useState<string | null>(null)
  const [shareNote, setShareNote] = useState<string | null>(null)
  const [done, setDone] = useState<{ id: string; courseCode: string | null } | null>(null)
  /** True only while a drag carrying files is over the zone — see `dragDepth` below. */
  const [dragging, setDragging] = useState(false)
  /** What a drop refused to do, in words. Null when the last drop was taken. */
  const [dropNote, setDropNote] = useState<string | null>(null)

  /** The registered job, kept so a failed upload retries instead of registering a second one. */
  const registered = useRef<RegisteredUpload | null>(null)
  const controller = useRef<AbortController | null>(null)
  /** Set just before abort(), so the rejection that follows is read as a cancel, not a failure. */
  const cancelled = useRef(false)
  const fileInput = useRef<HTMLInputElement | null>(null)
  /**
   * How many nested elements the drag is currently inside.
   *
   * `dragenter`/`dragleave` fire for every child the pointer crosses, so a plain boolean flickers
   * off the moment the cursor moves from the zone onto the text inside it. Counting enters and
   * leaves is the standard fix: the highlight goes out when the count returns to zero, and not
   * before.
   */
  const dragDepth = useRef(0)

  // The date defaults to today ONCE, when the clock arrives — never on every render, or a change
  // the owner made would be overwritten by the app's own idea of today.
  useEffect(() => {
    if (clock?.date && !date) setDate(clock.date)
  }, [clock?.date, date])

  /**
   * Take a file, and read its real duration if the browser can do it cheaply.
   *
   * `loadedmetadata` on an object URL reads the container header — no decode, no download, and it
   * works on a file that is already local. When it does not fire (an unsupported container, a
   * browser that refuses), the duration stays null and no screen ever states one. It is NOT
   * derived from the byte count: that would be wrong by whatever bitrate the phone chose.
   */
  const pick = useCallback((incoming: File) => {
    setFile(incoming)
    setDurationSec(null)
    setProblem(null)
    setDropNote(null)
    setDone(null)
    setSent(0)
    registered.current = null

    try {
      const url = URL.createObjectURL(incoming)
      const probe = new Audio()
      const finish = () => URL.revokeObjectURL(url)
      probe.onloadedmetadata = () => {
        if (Number.isFinite(probe.duration) && probe.duration > 0) {
          setDurationSec(Math.round(probe.duration))
        }
        finish()
      }
      probe.onerror = finish
      probe.src = url
    } catch {
      // A browser with no object URLs still uploads perfectly well; it just says nothing about
      // how long the recording is.
    }
  }, [])

  // ── drag and drop ─────────────────────────────────────────────────────────
  // The third way a file gets here, after the picker and the Android share sheet. It exists
  // because the owner is as often at a desktop as on the bus, and opening a file dialogue to
  // find a file he is already looking at in Explorer is a step that buys nothing.

  const uploading = upload.isPending

  /**
   * A file dropped ANYWHERE ELSE on this screen must do nothing at all.
   *
   * Left alone, a browser opens a dropped file in the tab — which navigates away from a
   * half-filled form and loses the course and the day with it. A near miss is the likeliest
   * mistake here (the zone is one box on a tall screen), so a miss is swallowed rather than
   * punished. Scoped to this route: it is torn down when the screen unmounts, so the Calendar's
   * own drag-and-drop is untouched.
   */
  useEffect(() => {
    const swallow = (event: DragEvent) => {
      if (!carriesFiles(event.dataTransfer)) return
      event.preventDefault()
    }
    window.addEventListener('dragover', swallow)
    window.addEventListener('drop', swallow)
    return () => {
      window.removeEventListener('dragover', swallow)
      window.removeEventListener('drop', swallow)
    }
  }, [])

  const onDragEnter = useCallback(
    (event: ReactDragEvent<HTMLLabelElement>) => {
      if (uploading || !carriesFiles(event.dataTransfer)) return
      event.preventDefault()
      dragDepth.current += 1
      setDragging(true)
    },
    [uploading],
  )

  const onDragOver = useCallback(
    (event: ReactDragEvent<HTMLLabelElement>) => {
      if (uploading || !carriesFiles(event.dataTransfer)) return
      // Without this the drop never fires at all — the default action wins.
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    [uploading],
  )

  const onDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDragging(false)
  }, [])

  /**
   * Take the dropped recording — and when it cannot, SAY SO rather than doing nothing.
   *
   * A dropzone that silently ignores a bad drop is indistinguishable from one that is broken, and
   * the owner would be left dragging the same file again. Every refusal below names the file and
   * the reason.
   */
  const onDrop = useCallback(
    (event: ReactDragEvent<HTMLLabelElement>) => {
      event.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      if (uploading) return

      const dropped = Array.from(event.dataTransfer?.files ?? [])
      if (dropped.length === 0) {
        setDropNote(
          'That drop carried no file — some apps hand over a link instead of the recording. ' +
            'Save it to disk first, then drag it in.',
        )
        return
      }

      const audio = dropped.find(looksLikeAudio)
      if (!audio) {
        setDropNote(
          dropped.length === 1
            ? `“${dropped[0].name}” is not an audio file, so nothing was picked.`
            : 'None of those are audio files, so nothing was picked.',
        )
        return
      }

      pick(audio)
      // `pick` clears the note; this re-states it, because taking one of several files silently
      // would look exactly like having taken all of them.
      if (dropped.length > 1) {
        setDropNote(`Dropped ${dropped.length} files — took “${audio.name}”. One at a time.`)
      }
    },
    [pick, uploading],
  )

  // ── the share target's other half ─────────────────────────────────────────
  //
  // The service worker staged the shared file under its own key and redirected here. THIS SCREEN
  // NO LONGER TAKES IT: `useUploadQueue` above imports every staged share into IndexedDB and
  // deletes the stage only after that write commits (lib/upload-queue.ts). A share is a recording
  // the owner has already decided to send, and the old behaviour — pre-filling a form and waiting
  // for a tap — meant closing the tab lost it.
  //
  // `?shared=1` therefore needs no handler at all; it is dropped from the URL, and the row
  // appears in the queue above. The other two values still have something to say.
  const shared = params.get('shared')
  useEffect(() => {
    if (!shared) return
    if (shared !== '1') {
      setShareNote(
        shared === 'empty'
          ? 'That share carried no audio file, so there is nothing to upload. Pick a recording below.'
          : 'That share could not be saved — most likely this phone is out of storage. The ' +
              'recording is untouched in the recorder app; pick it below.',
      )
    }
    const next = new URLSearchParams(params)
    next.delete('shared')
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared])

  const tooBig = file !== null && file.size > MAX_UPLOAD_BYTES

  const send = useCallback(
    (resume?: RegisteredUpload) => {
      if (!file || !date) return
      cancelled.current = false
      const abort = new AbortController()
      controller.current = abort
      setProblem(null)
      setSent(0)

      upload.mutate(
        {
          file,
          courseCode: courseCode || null,
          date: date as ISODate,
          durationSec,
          onProgress: (bytes) => setSent(bytes),
          signal: abort.signal,
          resume,
          onRegistered: (job) => {
            registered.current = job
          },
        },
        {
          onSuccess: (lecture) => {
            setDone({ id: lecture.id, courseCode: lecture.courseCode })
            setFile(null)
            setDurationSec(null)
            setSent(0)
            registered.current = null
            if (fileInput.current) fileInput.current.value = ''
          },
          onError: (error) => {
            // A cancel is the owner's own decision, not a failure to report back to him. The file
            // stays picked either way; only the red panel differs.
            if (cancelled.current) {
              setSent(0)
              return
            }
            setProblem(
              error instanceof Error ? error.message : 'The upload did not go through.',
            )
          },
        },
      )
    },
    [courseCode, date, durationSec, file, upload],
  )

  const total = file?.size ?? 0
  const percent = total > 0 ? Math.min(100, Math.round((sent / total) * 100)) : 0

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <header className="flex flex-none flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface px-4 pb-4 pt-5 sm:px-[30px]">
        <div className="flex min-w-0 flex-col gap-[5px]">
          <h1 className="text-[16px] font-semibold leading-none tracking-[-0.01em]">
            Upload a lecture
          </h1>
          <p className="text-[11.5px] font-normal leading-none text-microcopy">
            It transcribes on your desktop PC, not here
          </p>
        </div>
        <div className="ml-auto flex flex-none items-center gap-2">
          <DataModeBadge />
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[560px] flex-col gap-4 px-4 pb-10 pt-4 sm:px-[30px]">
        {shareNote && (
          <p className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[12px] leading-[1.45] text-warning text-pretty">
            {shareNote}
          </p>
        )}

        {/* ── the queue ────────────────────────────────────────────────── */}
        {/* Above the picker deliberately: a recording that is already on its way is the first
            thing the owner came here to check on. */}
        <UploadQueueSection queue={queue} lectures={recent ?? []} />

        {/* ── 1 · the file ─────────────────────────────────────────────── */}
        <section className="flex flex-col gap-[10px]">
          <FieldLabel n={1}>The recording</FieldLabel>

          <label
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={[
              'flex min-h-[92px] cursor-pointer flex-col justify-center gap-1 rounded-card border border-dashed px-4 py-4',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              dragging
                ? 'border-faint bg-fill'
                : file
                  ? 'border-line bg-surface'
                  : 'border-dash bg-surface-2',
            ].join(' ')}
          >
            <input
              ref={fileInput}
              type="file"
              accept="audio/*"
              className="sr-only"
              disabled={uploading}
              onChange={(event) => {
                const incoming = event.target.files?.[0]
                if (incoming) pick(incoming)
              }}
            />
            {dragging ? (
              <span className="text-[14px] font-semibold leading-[1.3]">Drop the recording here</span>
            ) : file ? (
              <>
                <span className="break-all text-[14px] font-semibold leading-[1.3]">
                  {file.name}
                </span>
                <span className="text-[12px] leading-none text-muted">
                  {megabytes(file.size)}
                  {durationSec !== null && ` · ${clockLength(durationSec)}`}
                  {' · tap to pick a different file'}
                </span>
              </>
            ) : (
              <>
                <span className="text-[14px] font-semibold leading-[1.3]">Choose a recording</span>
                <span className="text-[12px] leading-[1.45] text-muted text-pretty">
                  Any audio file on this phone. Or share one straight from Samsung Voice Recorder —
                  LifeOS is in its Share sheet once this app is installed.
                </span>
                {/* Only where a drag is possible at all. A phone has no Finder to drag out of, and
                    an instruction that cannot be followed is worse than no instruction. */}
                <span className="hidden text-[12px] leading-[1.45] text-muted text-pretty [@media(pointer:fine)]:block">
                  On a computer, drag it straight in from Finder or File Explorer.
                </span>
              </>
            )}
          </label>

          {dropNote && (
            <p
              role="status"
              className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[12px] leading-[1.45] text-warning text-pretty"
            >
              {dropNote}
            </p>
          )}

          {tooBig && (
            <p className="rounded-row border border-danger-line bg-danger-fill px-3 py-[9px] text-[12px] leading-[1.45] text-danger text-pretty">
              That file is {megabytes(file.size)} and one upload cannot exceed{' '}
              {megabytes(MAX_UPLOAD_BYTES)} — that is Cloudflare’s request-body limit, not a
              setting here. Split the recording, or drop the file into the desktop worker’s folder,
              which does not go through this endpoint at all.
            </p>
          )}
        </section>

        {/* ── 2 · the course ───────────────────────────────────────────── */}
        <section className="flex flex-col gap-[10px]">
          <FieldLabel n={2}>Which class</FieldLabel>
          <select
            value={courseCode}
            disabled={uploading}
            onChange={(event) => setCourseCode(event.target.value)}
            className="min-h-[46px] w-full rounded-button border border-line-2 bg-surface px-3 text-[14px] leading-none text-ink shadow-field"
          >
            <option value={NO_COURSE}>No course — assign later</option>
            {(courses ?? []).map((course) => (
              <option key={course.id} value={course.code}>
                {course.code} · {course.name}
              </option>
            ))}
          </select>
          <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
            {coursesError
              ? 'Your course list could not be read, so only “assign later” is offered. The upload still works — file it from the recording’s page afterwards.'
              : courseCode === NO_COURSE
                ? 'Nothing guesses this. An unfiled recording is listed below and is one tap from being filed.'
                : 'Its transcript becomes study material for this course, so it is worth being sure.'}
          </p>
        </section>

        {/* ── 3 · the date ─────────────────────────────────────────────── */}
        <section className="flex flex-col gap-[10px]">
          <FieldLabel n={3}>The day the class happened</FieldLabel>
          <input
            type="date"
            value={date}
            disabled={uploading}
            onChange={(event) => setDate(event.target.value)}
            className="min-h-[46px] w-full rounded-button border border-line-2 bg-surface px-3 text-[14px] leading-none text-ink shadow-field"
          />
          <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
            Today unless you change it — the day of the class, not the day you upload it.
          </p>
        </section>

        {/* ── the action, and everything it can become ─────────────────── */}
        {!uploading && !done && (
          <button
            type="button"
            disabled={!file || tooBig || !date || isDemoMode}
            onClick={() => send()}
            title={
              isDemoMode
                ? 'Demo mode has no upload path: a recording goes to R2 and is transcribed by the desktop PC, and neither exists without an account. The recordings below are sample data.'
                : undefined
            }
            style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
            className={[
              'min-h-[50px] w-full rounded-button px-4 text-[14px] font-semibold leading-none',
              !file || tooBig || !date || isDemoMode
                ? 'cursor-not-allowed bg-fill text-faint-2'
                : 'mo-ink mo-press bg-ink text-on-dark',
            ].join(' ')}
          >
            {isDemoMode ? 'Uploading needs an account' : 'Send it to the transcription queue'}
          </button>
        )}

        {isDemoMode && !uploading && !done && (
          <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
            Demo mode has no upload path — a recording’s life is cloud storage plus a GPU on your
            desktop. The recordings listed below are sample data.
          </p>
        )}

        {uploading && (
          <section
            aria-live="polite"
            className="flex flex-col gap-[10px] rounded-card border border-line bg-surface px-4 py-4 shadow-card"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13px] font-semibold leading-none">Uploading</span>
              <span className="text-[12px] font-semibold leading-none tabular-nums text-ink-2">
                {percent}%
              </span>
            </div>
            {/* The bar is the bytes the browser actually handed to the socket. It has no timer. */}
            <div className="h-[7px] w-full overflow-hidden rounded-pill bg-fill">
              <div
                className="h-full rounded-pill bg-school transition-[width] duration-[var(--dur-fast)] ease-[var(--ease-out)]"
                style={{ width: `${percent}%` }}
              />
            </div>
            <p className="text-[11.5px] leading-[1.45] text-muted text-pretty">
              {megabytes(sent)} of {megabytes(total)} sent. Leaving this page stops the upload —
              the recording itself is untouched on your phone.
            </p>
            <button
              type="button"
              onClick={() => {
                cancelled.current = true
                controller.current?.abort()
              }}
              style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
              className="mo-ink mo-press min-h-[44px] w-full rounded-button border border-line bg-surface px-4 text-[13px] font-medium leading-none text-ink-2"
            >
              Cancel this upload
            </button>
          </section>
        )}

        {problem && (
          <section className="flex flex-col gap-2 rounded-card border border-danger-line bg-danger-fill px-4 py-[14px]">
            <p className="text-[12px] leading-[1.45] text-danger text-pretty">{problem}</p>
            {/* Exactly one action, and it reuses the job that already exists — a retry, not a
                second registration. */}
            {file && (
              <button
                type="button"
                onClick={() => send(registered.current ?? undefined)}
                style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
                className="mo-ink mo-press min-h-[44px] w-full rounded-button bg-danger-solid px-4 text-[13px] font-semibold leading-none text-on-dark"
              >
                Try the upload again
              </button>
            )}
          </section>
        )}

        {done && (
          <section className="flex flex-col gap-2 rounded-card border border-success-line bg-success-fill px-4 py-[14px]">
            <p className="text-[13px] font-semibold leading-none text-success">
              Uploaded and queued
            </p>
            <p className="text-[12px] leading-[1.45] text-success text-pretty">
              The desktop worker transcribes it the next time your PC is awake — usually a few
              minutes. Nothing more to do here; the transcript appears on the recording’s page.
              {done.courseCode === null && ' It has no course yet, which you can fix there too.'}
            </p>
            <div className="flex flex-col gap-2 pt-1 sm:flex-row">
              <Link
                to={`/recordings/${done.id}`}
                style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
                className="mo-ink mo-press flex min-h-[44px] flex-1 items-center justify-center rounded-button bg-ink px-4 text-[13px] font-semibold leading-none text-on-dark"
              >
                Open the recording
              </Link>
              <button
                type="button"
                onClick={() => setDone(null)}
                style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
                className="mo-ink mo-press flex min-h-[44px] flex-1 items-center justify-center rounded-button border border-line bg-surface px-4 text-[13px] font-medium leading-none text-ink-2"
              >
                Upload another
              </button>
            </div>
          </section>
        )}

        {/* ── what is already in ───────────────────────────────────────── */}
        {recent && recent.length > 0 && (
          <section className="flex flex-col gap-[10px] pt-2">
            <FieldLabel>Recent recordings</FieldLabel>
            <ul className="flex flex-col gap-2">
              {recent.map((lecture) => (
                <li key={lecture.id}>
                  <RecordingRow lecture={lecture} />
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// THE QUEUE, ON SCREEN
//
// Every word below is the server's own state or a local one this app can prove. There is no
// "uploading in the background" anywhere, because nothing uploads while the app is closed; there
// is no "resuming", because a reopen sends the whole file again; and there is no "your PC is off",
// because this screen has no way to know that — `queued` means the recording is in the cloud and
// the desktop worker has not picked it up yet, which is all it says.
// ============================================================================

/** Where one queued recording stands, in the owner's words. Exported so the copy is asserted. */
export function stateLabel(entry: QueueEntry): string {
  switch (entry.state) {
    case 'saved_locally':
      return 'Saved on this phone'
    case 'registering':
      return 'Telling the server about it'
    case 'awaiting_audio':
      // "Again", never "resuming": the whole file goes up from the start every time.
      return entry.attempts > 1 ? 'Sending again, from the start' : 'Ready to send'
    case 'uploading':
      return 'Sending'
    case 'queued':
      return 'Uploaded. Waiting for the PC to transcribe.'
    case 'claimed':
      return 'Your PC has picked it up'
    case 'transcribing':
      return 'Transcribing on your PC'
    case 'done':
      return 'Transcribed'
    case 'failed':
      return 'The transcription failed'
    case 'cancelled':
      return 'Cancelled'
  }
}

/** Whether this row's remedy is a retry or a brand-new upload. */
function needsFreshUpload(entry: QueueEntry): boolean {
  return entry.state === 'failed' || entry.state === 'cancelled' || entry.needsNewIdentity === true
}

export function UploadQueueSection({
  queue,
  lectures,
}: {
  queue: ReturnType<typeof useUploadQueue>
  lectures: CapturedLecture[]
}) {
  if (queue.entries.length === 0 && queue.note === null) return null

  return (
    <section className="flex flex-col gap-[10px]">
      <FieldLabel>On its way</FieldLabel>
      {queue.note && (
        <p
          role="status"
          className="rounded-row border border-warning-line bg-warning-fill px-3 py-[9px] text-[12px] leading-[1.45] text-warning text-pretty"
        >
          {queue.note}
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {queue.entries.map((entry) => (
          <li key={entry.id}>
            <QueueRow
              entry={entry}
              courseCode={queueRowCourse(entry, lectures)}
              onRetry={() => queue.retry(entry.id)}
              onRemove={() => queue.remove(entry.id)}
              onSendAsNew={() => queue.sendAsNew(entry.id)}
            />
          </li>
        ))}
      </ul>
      <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
        These are saved on this phone and sent while LifeOS is open — not in the background. Tap
        one to try it now; press and hold to remove it from the queue.
      </p>
    </section>
  )
}

/**
 * The course a queue row names. Exported so the rule is asserted.
 *
 * The server's lecture wins once this screen has it — including a lecture the owner unfiled, whose
 * `null` is the newer truth. Until then the entry's own course speaks: the recorder KNEW which
 * class it was (the schedule named it at Stop), and "No course yet" under a recording the server
 * files as COGSCI C142 is the row denying what the phone knows (owner report, 2026-09-24). Neither
 * one set means nobody knew, and the row says exactly that. This screen still never invents one.
 */
export function queueRowCourse(entry: QueueEntry, lectures: CapturedLecture[]): string | null {
  const lecture = entry.lectureId ? lectures.find((l) => l.id === entry.lectureId) : undefined
  if (lecture) return lecture.courseCode
  return entry.courseCode ?? null
}

/** Milliseconds a press has to last before it counts as "hold to delete". */
const LONG_PRESS_MS = 550

function QueueRow({
  entry,
  courseCode,
  onRetry,
  onRemove,
  onSendAsNew,
}: {
  entry: QueueEntry
  courseCode: string | null
  onRetry: () => void
  onRemove: () => void
  onSendAsNew: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const held = useRef<number | null>(null)
  /** Set by the long press, so the tap that ends it does not also fire the retry. */
  const consumed = useRef(false)

  const startHold = useCallback(() => {
    consumed.current = false
    held.current = window.setTimeout(() => {
      consumed.current = true
      setConfirming(true)
    }, LONG_PRESS_MS)
  }, [])

  const endHold = useCallback(() => {
    if (held.current !== null) window.clearTimeout(held.current)
    held.current = null
  }, [])

  useEffect(() => endHold, [endHold])

  const fresh = needsFreshUpload(entry)

  return (
    <div className="flex flex-col gap-2 rounded-card border border-line bg-surface px-3 py-[10px]">
      <button
        type="button"
        onPointerDown={startHold}
        onPointerUp={endHold}
        onPointerLeave={endHold}
        onPointerCancel={endHold}
        onContextMenu={(event) => {
          // Android turns a long press into a context menu too. Same intent, same answer.
          event.preventDefault()
          setConfirming(true)
        }}
        onClick={() => {
          if (consumed.current) return
          if (!fresh) onRetry()
        }}
        className="flex min-h-[44px] w-full flex-col items-start gap-[3px] text-left"
      >
        <span className="break-all text-[13px] font-semibold leading-[1.3]">{entry.name}</span>
        <span className="text-[11.5px] leading-[1.45] text-muted text-pretty">
          {stateLabel(entry)}
        </span>
        <span className="text-[11.5px] leading-none text-microcopy">
          {megabytes(entry.size)}
          {' · '}
          {courseCode ?? 'No course yet'}
          {/* Whether the phone still holds the audio is the single most load-bearing fact on
              this row, so it is stated rather than implied by the status. */}
          {entry.blob === null ? ' · phone copy released' : ' · still on this phone'}
        </span>
      </button>

      {entry.lastError && (
        <p className="rounded-row border border-danger-line bg-danger-fill px-[10px] py-[7px] text-[11.5px] leading-[1.45] text-danger text-pretty">
          {entry.lastError}
        </p>
      )}

      {fresh && entry.blob !== null && (
        <button
          type="button"
          onClick={onSendAsNew}
          style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
          className="mo-ink mo-press min-h-[44px] w-full rounded-button border border-line bg-surface px-3 text-[12px] font-medium leading-none text-ink-2"
        >
          Send as a new upload
        </button>
      )}

      {confirming && (
        <div className="flex flex-col gap-2 rounded-row border border-line-2 bg-surface-2 px-[10px] py-[9px]">
          <p className="text-[11.5px] leading-[1.45] text-ink-2 text-pretty">
            Remove “{entry.name}” from the queue?{' '}
            {entry.blob === null
              ? 'It has already been uploaded, so this only clears the row.'
              : 'LifeOS’s copy goes with it. The recording in your recorder app is untouched.'}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => {
                setConfirming(false)
                onRemove()
              }}
              style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
              className="mo-ink mo-press min-h-[44px] flex-1 rounded-button bg-danger-solid px-3 text-[12px] font-semibold leading-none text-on-dark"
            >
              Remove it
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
              className="mo-ink mo-press min-h-[44px] flex-1 rounded-button border border-line bg-surface px-3 text-[12px] font-medium leading-none text-ink-2"
            >
              Keep it
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** The numbered step label. The number is dropped for sections that are not a step. */
function FieldLabel({ n, children }: { n?: number; children: ReactNode }) {
  return (
    <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase leading-none tracking-[0.06em] text-muted">
      {n !== undefined && (
        <span className="flex size-[18px] flex-none items-center justify-center rounded-full bg-fill text-[10px] font-semibold text-ink-2">
          {n}
        </span>
      )}
      {children}
    </h2>
  )
}

/** '37.4 MB'. One decimal, because 'a 37 MB file' and 'a 37.4 MB file' answer the same question. */
export function megabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
